import { useEffect, useRef } from 'react';
import { usePulseStore } from './usePulseStore';
import type { PulseContentItem, PulseGridConfig } from '../types';

/**
 * Periodically recalculates content ranking and re-sorts the grid.
 * Ensures visible tile movement every cycle by combining real engagement
 * scores with controlled random swaps.
 */
export function usePulseRanking(config: PulseGridConfig) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cycleRef = useRef(0);

  useEffect(() => {
    const run = () => {
      const { items } = usePulseStore.getState();
      if (items.length < 2) return;

      cycleRef.current += 1;
      const cycle = cycleRef.current;

      const now = Date.now();
      const reranked = items
        .map((item) => ({
          ...item,
          rankScore: computeRankScore(item, now),
        }))
        .sort((a, b) => b.rankScore - a.rankScore);

      // Force visible movement: every cycle, swap 2-4 random adjacent pairs.
      // This creates the "alive" feeling even when engagement scores are static.
      const swapCount = 2 + Math.floor(Math.random() * 3); // 2-4 swaps
      for (let s = 0; s < swapCount; s++) {
        const i = Math.floor(Math.random() * (reranked.length - 1));
        const a = reranked[i];
        const b = reranked[i + 1];
        if (!a || !b) continue;
        // Only swap if both items are in normal states (not moderated/deleted).
        if (a.state !== 'deleted' && a.state !== 'moderated'
            && b.state !== 'deleted' && b.state !== 'moderated') {
          reranked[i] = b;
          reranked[i + 1] = a;
        }
      }

      // Every 3rd cycle, do a larger shuffle: move one item from bottom half to top half.
      if (cycle % 3 === 0 && reranked.length > 6) {
        const fromIdx = Math.floor(reranked.length / 2) + Math.floor(Math.random() * (reranked.length / 2));
        const toIdx = Math.floor(Math.random() * Math.floor(reranked.length / 3));
        const candidate = reranked[fromIdx];
        if (candidate && (candidate.state === 'active' || candidate.state === 'new')) {
          reranked.splice(fromIdx, 1);
          reranked.splice(toIdx, 0, candidate);
        }
      }

      usePulseStore.setState({ items: reranked });
    };

    // First shuffle after a brief delay (let initial render settle).
    const initialTimer = setTimeout(run, 2000);
    intervalRef.current = setInterval(run, config.rankIntervalSec * 1000);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [config.rankIntervalSec]);
}

/**
 * Composite ranking score.
 */
function computeRankScore(item: PulseContentItem, now: number): number {
  if (item.state === 'deleted') return -1000;
  if (item.state === 'moderated') return -500;

  const ageMs = now - new Date(item.uploadedAt).getTime();
  const ageMinutes = Math.max(1, ageMs / 60_000);

  const velocityScore = item.velocity * 10;
  const recencyScore = 80 * Math.exp(-ageMinutes / 20);
  const stateMult = STATE_PRIORITY[item.state] ?? 1.0;
  const tipScore = item.tipsTotal * 5;
  const viewScore = item.viewsRecent > 0 ? Math.log10(item.viewsRecent) * 15 : 0;

  return (velocityScore + recencyScore + tipScore + viewScore) * stateMult;
}

const STATE_PRIORITY: Record<string, number> = {
  trending: 1.5,
  new: 1.3,
  active: 1.0,
  processing: 0.8,
  moderated: 0.1,
  deleted: 0.0,
};
